import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/utils/api";
import AdminDashboard from "@/components/dashboard/AdminDashboard";
import SchoolAdminDashboard from "@/components/dashboard/SchoolAdminDashboard";
import ClassTeacherDashboard from "@/components/dashboard/ClassTeacherDashboard";
import DoctorDashboard from "@/components/dashboard/DoctorDashboard";
import ParentDashboard from "@/components/dashboard/ParentDashboard";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Internal Admin",
  school_admin: "School Admin",
  class_teacher: "Class Teacher",
  doctor: "Doctor",
  parent: "Parent / Guardian",
};

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [resendStatus, setResendStatus] = useState(null);

  const resendVerification = async () => {
    setResendStatus("sending");
    try {
      await api.post("/api/auth/resend-verification");
      setResendStatus("sent");
    } catch { setResendStatus("error"); }
  };

  if (!user) return null;

  const Dash = {
    super_admin: AdminDashboard,
    admin: AdminDashboard,
    school_admin: SchoolAdminDashboard,
    class_teacher: ClassTeacherDashboard,
    doctor: DoctorDashboard,
    parent: ParentDashboard,
  }[user.role] || ParentDashboard;

  return (
    <div className="min-h-screen bg-[#0A1530] text-slate-100">
      <nav className="border-b border-slate-800/80 bg-[#0B1735]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="dashboard-logo">
            <img src="/Tots360_logo.png" alt="tots360" className="h-9 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-sm font-medium" data-testid="dashboard-user-name">{user.full_name}</span>
              <span className="text-xs text-slate-400" data-testid="dashboard-user-role">
                {ROLE_LABEL[user.role] || user.role}
              </span>
            </div>
            <button
              onClick={logout}
              data-testid="dashboard-logout-button"
              className="px-3 py-1.5 text-sm rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 transition"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {!user.email_verified && user.auth_provider === "password" && (
          <div
            className="mb-6 p-4 rounded-lg bg-amber-400/10 border border-amber-400/30 flex items-center justify-between"
            data-testid="email-verify-banner"
          >
            <div>
              <p className="text-sm font-medium text-amber-200">Your email is not verified.</p>
              <p className="text-xs text-amber-100/80 mt-0.5">
                Check your inbox for a verification link (valid for 24 hours).
              </p>
            </div>
            <button
              onClick={resendVerification}
              disabled={resendStatus === "sending"}
              data-testid="resend-verification-button"
              className="px-3 py-1.5 text-sm rounded-md bg-amber-400/20 hover:bg-amber-400/30 text-amber-100 border border-amber-400/30 disabled:opacity-60"
            >
              {resendStatus === "sending" ? "Sending…" : resendStatus === "sent" ? "Email sent ✓" : "Resend email"}
            </button>
          </div>
        )}

        <Dash user={user} />
      </main>
    </div>
  );
}
